import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`)
      .then((r) => { setProduct(r.data); setSize(r.data.sizes?.[0] || null); setColor(r.data.colors?.[0] || null); setActiveImg(0); })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="max-w-[1500px] mx-auto px-5 py-24 text-center">Product not found. <Link to="/shop" className="text-accent underline">Back to shop</Link></div>;
  if (!product) return <div className="py-24 text-center font-mono text-sm text-muted-foreground">Loading…</div>;

  const gallery = (product.images && product.images.length > 0) ? product.images : [product.image];

  const handleAdd = () => {
    addItem(product, size, color, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-8">
      <Link to="/shop" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground hover:text-accent mb-6"><ArrowLeft size={14} /> Back to shop</Link>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 border-t border-border pt-8">
        <div>
          <div className="bg-secondary overflow-hidden">
            <img src={gallery[activeImg]} alt={product.name} className="w-full aspect-[3/4] object-cover" data-testid="product-image" />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-3 flex-wrap" data-testid="product-thumbnails">
              {gallery.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumbnail-${i}`}
                  className={`w-20 h-24 bg-secondary overflow-hidden border transition-colors ${activeImg === i ? "border-foreground" : "border-transparent hover:border-border"}`}>
                  <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="md:py-4">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-accent">{product.category}</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter uppercase mt-3" data-testid="product-name">{product.name}</h1>
          <div className="font-mono text-2xl mt-4" data-testid="product-price">£{product.price.toFixed(2)}</div>
          <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">{product.description}</p>

          {product.sizes?.length > 0 && (
            <div className="mt-8">
              <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Size</div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`w-14 h-12 text-sm font-medium border transition-colors ${size === s ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-8">
              <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Colour{color ? `: ${color}` : ""}</div>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                    className={`px-4 h-12 text-sm font-medium border transition-colors ${color === c ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.size_guide?.length > 0 && (
            <details className="mt-6 border border-border" data-testid="size-guide">
              <summary className="cursor-pointer select-none px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-secondary">Size Guide (cm)</summary>
              <div className="overflow-x-auto border-t border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-secondary text-[10px] uppercase tracking-wider">
                      <th className="text-left px-4 py-2 font-bold">Size</th>
                      <th className="text-left px-4 py-2 font-bold">Chest</th>
                      <th className="text-left px-4 py-2 font-bold">Length</th>
                      <th className="text-left px-4 py-2 font-bold">Waist</th>
                      <th className="text-left px-4 py-2 font-bold">Sleeve</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.size_guide.map((r, i) => (
                      <tr key={i} className="border-t border-border font-mono">
                        <td className="px-4 py-2 font-medium">{r.size || "—"}</td>
                        <td className="px-4 py-2">{r.chest || "—"}</td>
                        <td className="px-4 py-2">{r.length || "—"}</td>
                        <td className="px-4 py-2">{r.waist || "—"}</td>
                        <td className="px-4 py-2">{r.sleeve || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <button onClick={handleAdd} data-testid="add-to-cart-button"
            className="w-full mt-10 bg-foreground text-background py-5 text-xs font-bold uppercase tracking-[0.25em] hover:bg-black/80 transition-colors">
            Add to Cart — £{product.price.toFixed(2)}
          </button>
          <div className="mt-4 text-xs text-muted-foreground font-mono uppercase tracking-wider">Free shipping over £150 · 30-day returns</div>
        </div>
      </div>
    </div>
  );
}
